/**
 * ListCompletionTable: #54 « Module List Completion dans les performances ».
 *
 * Doléance : « Nouveau tableau de bord dans Performances dédié au suivi de
 * complétion des listes. Filtres : par utilisateur, par liste. Colonnes : Nom
 * liste, Health (New/In Progress), Completion %, Contacts total, Restants,
 * Supprimés/disabled, Connectés, % Connectés, Nb appels effectués, Moyenne
 * appels/contact, Nb RDVs, Date dernier appel. Bouton "Refresh". »
 *
 * Ce qu'est une « liste » ici : la SOURCE du prospect (`prospectsBySource`),
 * la notion déjà employée par le filtre « Toutes les listes » du dialer. On ne
 * réinvente pas un regroupement, contrairement à #81, qui demandait une
 * appartenance de liste sur la fiche contact et pour laquelle AUCUNE donnée
 * n'existe côté front.
 *
 * Le filtre « par utilisateur » porte sur les APPELS (agent_email), pas sur les
 * prospects : un prospect n'appartient à personne, c'est l'appel qui a un
 * auteur. Conséquence assumée et visible à l'écran : filtrer par agent change
 * les colonnes d'activité (appels, connectés, RDV, dernier appel) mais pas le
 * nombre de contacts de la liste.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { currentLocale, formatNumber, formatPercent } from '@/i18n/locale';
import type { ListCompletionRow } from '@/api/endpoints/analytics';
import { LoadError } from './LoadError';

/*
 * Since W4 (T-d3, CEO-10) the rows come from the server
 * (POST /api/analytics/dialer/list-completion): every contact of every list
 * and every call, where the table used to cross the 1000 contacts and 500
 * calls the browser had loaded. « Restants » no longer counts the blocked
 * contacts; « Épuisés » (the attempt limit reached without a meeting) is new
 * (Minari parity, audit B2-analytics).
 */

interface Props {
  rows: ListCompletionRow[];
  state: 'loading' | 'error' | 'ready';
  onRefresh: () => void;
  error?: unknown;
}

/** Health : New = aucun appel, Terminée = 100 %, sinon In Progress. */
function health(pct: number, appels: number, t: (key: string) => string): { label: string; cls: string } {
  if (appels === 0) return { label: t('pages:analytics.listCompletion.health.new'), cls: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200' };
  if (pct >= 100) return { label: t('pages:analytics.listCompletion.health.done'), cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200' };
  return { label: t('pages:analytics.listCompletion.health.inProgress'), cls: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200' };
}

export default function ListCompletionTable({ rows, state, onRefresh, error }: Props) {
  const { t } = useTranslation(['pages', 'common', 'dialer']);

  const lignes = useMemo(() => rows.map((r) => {
    const pct = r.completion === null ? 0 : Math.round(r.completion * 100);
    return {
      src: r.list || t('dialer:analytics.lists.noList'),
      key: r.list,
      total: r.total,
      restants: r.remaining,
      desactives: r.disabled,
      epuises: r.exhausted,
      pct,
      connectes: r.connectedContacts,
      pctConnectes: r.total > 0 ? (r.connectedContacts / r.total) * 100 : null,
      appelsEffectues: r.calls,
      moyenne: r.total > 0 ? r.calls / r.total : null,
      rdv: r.meetings,
      dernier: r.lastCallAt ? new Date(r.lastCallAt).getTime() : null,
      sante: health(pct, r.calls, t),
    };
  }), [rows, t]);

  return (
    <div className="space-y-4">
      {/* Filtres + Refresh */}
      <div className="flex flex-wrap items-center gap-2">
        <ListChecks className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-sm font-medium text-foreground mr-1">{t('pages:analytics.listCompletion.title')}</h2>

        <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={onRefresh} title={t('pages:analytics.listCompletion.refreshTitle')}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden />
          {t('pages:analytics.listCompletion.refresh')}
        </Button>
      </div>

      {/* Tableau */}
      <div className="rounded-lg border border-border bg-card">
        <div className="overflow-x-auto vf-scroll-visible rounded-lg">
          <table className="w-full text-sm" data-testid="table-list-completion">
            <thead>
              <tr className="border-b border-border">
                {[
                  t('pages:analytics.listCompletion.columns.listName'),
                  t('pages:analytics.listCompletion.columns.health'),
                  t('pages:analytics.listCompletion.columns.completion'),
                  t('pages:analytics.listCompletion.columns.contacts'),
                  t('pages:analytics.listCompletion.columns.remaining'),
                  t('pages:analytics.listCompletion.columns.disabled'),
                  t('dialer:analytics.lists.exhausted'),
                  t('pages:analytics.listCompletion.columns.connected'),
                  t('pages:analytics.listCompletion.columns.connectedRate'),
                  t('pages:analytics.listCompletion.columns.calls'),
                  t('pages:analytics.listCompletion.columns.avgCallsPerContact'),
                  t('pages:analytics.listCompletion.columns.meetings'),
                  t('pages:analytics.listCompletion.columns.lastCall'),
                ].map((c, i) => (
                  <th key={c} scope="col" className={`sticky top-0 bg-muted text-left px-3 py-2.5 text-xs font-medium text-muted-foreground whitespace-nowrap ${i === 0 ? 'left-0 z-20' : 'z-10'}`}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {state === 'loading' && lignes.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-6"><div className="h-16 animate-pulse motion-reduce:animate-none rounded bg-[var(--vf-surface-3)]" /></td>
                </tr>
              ) : state === 'error' ? (
                <tr>
                  <td colSpan={13} className="px-4 py-8">
                    <LoadError error={error} onRetry={onRefresh} className="justify-center" />
                  </td>
                </tr>
              ) : lignes.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    {t('pages:analytics.listCompletion.empty')}
                  </td>
                </tr>
              ) : lignes.map((l) => (
                <tr key={l.key} className="hover:bg-muted/20">
                  {/* Le nom de liste est souvent un nom d'import a rallonge : il est
                      tronque a une largeur fixe, le nom entier reste au survol, et
                      la colonne reste visible quand on fait defiler le tableau. */}
                  <td className="sticky left-0 z-10 bg-card px-3 py-2.5 font-medium text-foreground">
                    <span className="block max-w-[14rem] truncate" title={l.src}>{l.src}</span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.sante.cls}`}>{l.sante.label}</span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 rounded-full bg-muted overflow-hidden">
                        <div className="h-full" style={{ width: `${Math.min(100, l.pct)}%`, background: 'var(--vf-accent)' }} />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground">{formatPercent(l.pct, 0)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{formatNumber(l.total)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{formatNumber(l.restants)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{formatNumber(l.desactives)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{l.epuises === null ? '—' : formatNumber(l.epuises)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{formatNumber(l.connectes)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{l.pctConnectes === null ? '—' : formatPercent(l.pctConnectes)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{formatNumber(l.appelsEffectues)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{l.moyenne === null ? '—' : formatNumber(l.moyenne, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                  <td className="px-3 py-2.5 tabular-nums text-foreground/80">{formatNumber(l.rdv)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-muted-foreground whitespace-nowrap">
                    {l.dernier
                      ? new Date(l.dernier).toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : t('common:states.noValue')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
